import Select from 'src/shared/components/select/Select';
import React from 'react';
import { Trash2 } from 'lucide-react';
import Input from 'src/shared/components/input/Input';
import { SecondaryButton, TertiaryButton } from 'src/shared/components/button/Button';
import { TILE_GRID_CODE, isTileSourceMediaType } from 'src/shared/catalogue/tileSource';
import {
    ASSET_KEY_OPTIONS,
    DATA_CRS_CODE,
    MEDIA_TYPE_OPTIONS,
    ROLE_OPTIONS,
    isGeospatialMediaType,
} from '../../domain/assets/assetVocabulary';
import { emptyAssetForm, type AssetFormErrors, type AssetFormValues } from '../../domain/documents/stacItemDocument';

type AssetEditorProps = {
    assets: AssetFormValues[];
    /** Keyed by position, so a message stays with the asset it belongs to. */
    errors: Record<number, AssetFormErrors>;
    onChange: (assets: AssetFormValues[]) => void;
    disabled?: boolean;
};

/** The sentinel a select uses to mean "none of these". Not a value anything stores. */
const OTHER = '__other__';

/** The way out of both fixed lists: a key or a media type the vocabulary does not name. */
const OTHER_OPTION = { value: OTHER, label: 'Something else…' };

const required = <span className='ml-1 text-xs font-normal text-muted-foreground'>(required)</span>;
const optional = <span className='ml-1 text-xs font-normal text-muted-foreground'>(optional)</span>;

const label = (htmlFor: string, testId: string, text: React.ReactNode) => (
    <label htmlFor={htmlFor} className='text-sm font-medium text-primary' data-testid={testId}>
        {text}
    </label>
);

const hint = (text: React.ReactNode, testId?: string) => (
    <p className='text-xs text-muted-foreground' data-testid={testId}>
        {text}
    </p>
);

/**
 * The assets of one version.
 *
 * An asset is not a resource of its own — it lives inside the version document — so editing
 * assets here *is* editing the version. The engine `HEAD`s every href it is given, so an
 * asset must already exist on S3: this names an object, it does not upload one.
 *
 * Keys and roles are offered as lists to standardise what the catalogue reads like; neither is
 * something the engine enforces. Extension fields are not edited here — the whole document is
 * the escape hatch — but they are carried through an edit untouched. Media type and `proj:code` are different — the engine matches
 * both exactly — so those are stated plainly as requirements rather than left to be discovered.
 */
const AssetEditor = ({ assets, errors, onChange, disabled }: AssetEditorProps) => {
    const update = (index: number, changes: Partial<AssetFormValues>) =>
        onChange(assets.map((asset, position) => (position === index ? { ...asset, ...changes } : asset)));

    const removeAsset = (index: number) => onChange(assets.filter((_asset, position) => position !== index));

    return (
        <div className='flex flex-col gap-4'>
            {assets.length === 0 && (
                <p className='text-sm text-muted-foreground' data-testid='asset-editor-empty'>
                    A version needs at least one asset, or the engine has nothing to serve.
                </p>
            )}

            {assets.map((asset, index) => {
                const assetErrors = errors[index] ?? {};
                const isKnownKey = ASSET_KEY_OPTIONS.some(option => option.value === asset.key);
                const isKnownType = MEDIA_TYPE_OPTIONS.some(option => option.value === asset.type);
                const usesCustomKey = asset.key !== '' && !isKnownKey;
                const usesCustomType = asset.type !== '' && !isKnownType;
                const isTileSource = isTileSourceMediaType(asset.type);
                const isGeospatial = isGeospatialMediaType(asset.type);

                const chooseKey = (value: string) => {
                    if (value === OTHER) {
                        update(index, { key: ' ' });
                        return;
                    }
                    const suggestion = ASSET_KEY_OPTIONS.find(option => option.value === value)?.suggestedMediaType;
                    // Only fill a media type nobody has chosen; never overwrite one.
                    update(index, { key: value, ...(asset.type === '' && suggestion ? { type: suggestion } : {}) });
                };

                const toggleRole = (role: string) =>
                    update(index, {
                        roles: asset.roles.includes(role) ? asset.roles.filter(held => held !== role) : [...asset.roles, role],
                    });

                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <fieldset
                        key={index}
                        className='flex flex-col gap-4 rounded-md border border-border p-4'
                        data-testid={`asset-${index}`}
                    >
                        <div className='flex items-center justify-between'>
                            <legend className='text-sm font-semibold text-primary'>Asset {index + 1}</legend>
                            <TertiaryButton
                                type='button'
                                disabled={disabled}
                                className='text-red-100'
                                onClick={() => removeAsset(index)}
                                data-testid={`asset-remove-${index}`}
                            >
                                <Trash2 className='h-4 w-4' />
                            </TertiaryButton>
                        </div>

                        {/* The line a reader takes the asset in by, so it heads the box — where
                            the version form puts its own title. Optional: the key already names
                            the asset, and most producers have nothing better to add. */}
                        <div className='flex flex-col gap-1'>
                            {label(`asset-${index}-title`, `asset-${index}-title-label`, <>Title{optional}</>)}
                            <Input
                                id={`asset-${index}-title`}
                                data-testid={`asset-${index}-title`}
                                value={asset.title}
                                disabled={disabled}
                                onChange={event => update(index, { title: event.target.value })}
                            />
                        </div>

                        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                            <div className='flex flex-col gap-1'>
                                {label(`asset-${index}-key`, `asset-${index}-key-label`, <>Key{required}</>)}
                                <Select
                                    testId={`asset-${index}-key`}
                                    width='100%'
                                    placeholder='Choose a key…'
                                    isDisabled={disabled}
                                    isInvalid={Boolean(assetErrors.key)}
                                    selectedValue={usesCustomKey ? OTHER : asset.key || undefined}
                                    onChange={chooseKey}
                                    options={[...ASSET_KEY_OPTIONS.map(({ value, label: text }) => ({ value, label: text })), OTHER_OPTION]}
                                />
                                {usesCustomKey && (
                                    <Input
                                        data-testid={`asset-${index}-key-custom`}
                                        value={asset.key.trim()}
                                        disabled={disabled}
                                        placeholder='a key of your own'
                                        onChange={event => update(index, { key: event.target.value })}
                                    />
                                )}
                                {!assetErrors.key && hint('Names this asset within the version.')}
                                {assetErrors.key && (
                                    <p className='text-xs text-red-100' data-testid={`asset-${index}-key-error`}>
                                        {assetErrors.key}
                                    </p>
                                )}
                            </div>

                            <div className='flex flex-col gap-1'>
                                {label(`asset-${index}-type`, `asset-${index}-type-label`, <>Media type{required}</>)}
                                <Select
                                    testId={`asset-${index}-type`}
                                    width='100%'
                                    placeholder='Choose a media type…'
                                    isDisabled={disabled}
                                    isInvalid={Boolean(assetErrors.type)}
                                    selectedValue={usesCustomType ? OTHER : asset.type || undefined}
                                    onChange={chosen => update(index, { type: chosen === OTHER ? ' ' : chosen })}
                                    options={[
                                        ...MEDIA_TYPE_OPTIONS.map(({ value, label: text }) => ({ value, label: text })),
                                        OTHER_OPTION,
                                    ]}
                                />
                                {usesCustomType && (
                                    <Input
                                        data-testid={`asset-${index}-type-custom`}
                                        value={asset.type.trim()}
                                        disabled={disabled}
                                        placeholder='application/x-something'
                                        onChange={event => update(index, { type: event.target.value })}
                                    />
                                )}
                                {!assetErrors.type && hint('The engine reads this to decide what media it can serve.')}
                                {assetErrors.type && (
                                    <p className='text-xs text-red-100' data-testid={`asset-${index}-type-error`}>
                                        {assetErrors.type}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className='flex flex-col gap-1'>
                            {label(`asset-${index}-href`, `asset-${index}-href-label`, <>Object{required}</>)}
                            <Input
                                id={`asset-${index}-href`}
                                data-testid={`asset-${index}-href`}
                                value={asset.href}
                                disabled={disabled}
                                placeholder='s3://bucket/key'
                                onChange={event => update(index, { href: event.target.value })}
                            />
                            {!assetErrors.href && hint('Must already exist — the engine checks it can read it before cataloguing.')}
                            {assetErrors.href && (
                                <p className='text-xs text-red-100' data-testid={`asset-${index}-href-error`}>
                                    {assetErrors.href}
                                </p>
                            )}
                        </div>

                        {/* Offered on every asset — the projection of a file is worth recording
                            whatever it holds — but only a tile source is *required* to state
                            one, and only a tile source is pinned to a single grid. The engine
                            cannot learn an archive's grid from the archive: it reads the tile
                            *type* from the header and nothing about the matrix. A data file is
                            never opened, and carries its own CRS if anything ever does. */}
                        <div className='flex flex-col gap-1' data-testid={`asset-${index}-projCode-block`}>
                            {label(
                                `asset-${index}-projCode`,
                                `asset-${index}-projCode-label`,
                                <>
                                    proj:code
                                    {isGeospatial ? required : optional}
                                </>
                            )}
                            {hint(
                                isTileSource ? (
                                    <>
                                        The tile grid this archive was built on. The tile routes serve{' '}
                                        <code className='rounded bg-white px-1'>{TILE_GRID_CODE}</code> only — an archive built on another
                                        grid would draw in the wrong place.
                                    </>
                                ) : isGeospatial ? (
                                    `The projection this file’s coordinates are in. Required of anything holding coordinates. ${DATA_CRS_CODE} is the usual answer, but any code is accepted.`
                                ) : (
                                    'The projection this file’s coordinates are in.'
                                ),
                                `asset-${index}-projCode-explainer`
                            )}
                            <div className='flex items-center gap-2'>
                                <Input
                                    id={`asset-${index}-projCode`}
                                    data-testid={`asset-${index}-projCode`}
                                    value={asset.projCode}
                                    disabled={disabled}
                                    placeholder={isTileSource ? TILE_GRID_CODE : DATA_CRS_CODE}
                                    onChange={event => update(index, { projCode: event.target.value })}
                                />
                                {/* The tile grid is the rule for an archive; WGS84 is only the
                                    common case for data, so it is offered and not imposed. */}
                                {isGeospatial && (
                                    <TertiaryButton
                                        type='button'
                                        disabled={disabled}
                                        onClick={() => update(index, { projCode: isTileSource ? TILE_GRID_CODE : DATA_CRS_CODE })}
                                        data-testid={`asset-${index}-projCode-use-default`}
                                    >
                                        Use {isTileSource ? TILE_GRID_CODE : DATA_CRS_CODE}
                                    </TertiaryButton>
                                )}
                            </div>
                            {assetErrors.projCode && (
                                <p className='text-xs text-red-100' data-testid={`asset-${index}-projCode-error`}>
                                    {assetErrors.projCode}
                                </p>
                            )}
                        </div>

                        <div className='flex flex-col gap-2'>
                            <span className='text-sm font-medium text-primary'>Roles</span>
                            {hint(
                                'What this asset is used for, as metadata only, following OGC STAC conventions.',
                                `asset-${index}-roles-explainer`
                            )}
                            <div className='flex flex-wrap gap-2'>
                                {ROLE_OPTIONS.map(role => {
                                    const isChosen = asset.roles.includes(role.value);
                                    return (
                                        <button
                                            key={role.value}
                                            type='button'
                                            disabled={disabled}
                                            aria-pressed={isChosen}
                                            title={role.description}
                                            onClick={() => toggleRole(role.value)}
                                            data-testid={`asset-${index}-role-${role.value}`}
                                            className={[
                                                'rounded-full border px-3 py-1 text-xs transition-colors',
                                                isChosen
                                                    ? 'border-primary bg-primary text-white'
                                                    : 'border-border bg-white text-muted-foreground hover:border-primary',
                                            ].join(' ')}
                                        >
                                            {role.value}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </fieldset>
                );
            })}

            <div>
                <SecondaryButton
                    type='button'
                    disabled={disabled}
                    onClick={() => onChange([...assets, emptyAssetForm()])}
                    data-testid='asset-add'
                >
                    Add asset
                </SecondaryButton>
            </div>
        </div>
    );
};

export default AssetEditor;
